import type {
  AdminDeskResponse,
  AdminResource,
  AdminResourceListResponse,
  AdminResourceResponse,
  CreateAdminResourceRequest,
  ResourceDesk,
  ResourceKind,
  UpdateAdminResourceRequest,
} from '@mds/shared/types/resource';

import { api } from '@/lib/api';

/** The resource endpoints — see `backend/docs/resources.md`. */

/** A type alias, so it carries the index signature the client's `query` needs. */
export type ResourceQuery = {
  includeInactive?: boolean;
  kind?: ResourceKind;
  /** Matched across key, name and note. */
  q?: string;
};

export const listResources = (query: ResourceQuery = {}): Promise<AdminResource[]> =>
  api.get<AdminResourceListResponse>('admin/resources', { query }).then((r) => r.data);

export const getResource = (resourceId: string): Promise<AdminResource> =>
  api.get<AdminResourceResponse>(`admin/resources/${resourceId}`).then((r) => r.data);

export const createResource = (body: CreateAdminResourceRequest): Promise<AdminResource> =>
  api.post<AdminResourceResponse>('admin/resources', body).then((r) => r.data);

/**
 * Configuration only. Keys, kinds, facility links and seat maps are immutable
 * through this API, and prices are set on the rate card, not here.
 */
export const updateResource = (
  resourceId: string,
  body: UpdateAdminResourceRequest,
): Promise<AdminResource> =>
  api.patch<AdminResourceResponse>(`admin/resources/${resourceId}`, body).then((r) => r.data);

/**
 * The only thing that can be done to a desk. The seat map is static
 * application data, so desks are never created, renamed, reordered or deleted
 * through the API — `spaces-data.json` is the source of truth for the layout.
 */
export const setDeskActive = (
  resourceId: string,
  deskId: string,
  isActive: boolean,
): Promise<ResourceDesk> =>
  api
    .patch<AdminDeskResponse>(`admin/resources/${resourceId}/desks/${deskId}`, { isActive })
    .then((r) => r.data);
